import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from './Badge';
import { PiPlusLight, PiCloudArrowUpLight } from 'react-icons/pi';
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS } from './FieldShell';

interface SingleFileInputProps {
  label?: string;
  accept?: string;
  file: File | null;
  setFile: (file: File | null) => void;
  subMsg?: string;
  existingFileUrl?: string;
  desc?: string;
  disabled?: boolean;
}

const SingleFileInput: React.FC<SingleFileInputProps> = ({
  label,
  accept,
  file,
  setFile,
  subMsg,
  existingFileUrl,
  desc,
  disabled,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isImageType = accept && accept.includes('image');

  useEffect(() => {
    if (file) {
      if (isImageType) {
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
        return () => {
          if (objectUrl.startsWith('blob:')) {
            URL.revokeObjectURL(objectUrl);
          }
        };
      } else {
        setPreviewUrl(null);
      }
    } else if (existingFileUrl && isImageType) {
      setPreviewUrl(existingFileUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [file, existingFileUrl, isImageType]);

  const handleAddFile = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
    }
  };

  const getFileName = (): string => {
    if (file) return file.name;
    if (existingFileUrl) {
      const parts = existingFileUrl.split('/');
      return parts[parts.length - 1] || t('Existing file');
    }
    return '';
  };

  return (
    <div className='glass rounded-lg p-4'>
      {label && <label className={FIELD_LABEL_CLASS}>{t(label)}</label>}
      {desc && <p className={`${FIELD_HELP_CLASS} text-start`}>{t(desc)}</p>}
      <div className='space-y-1'>
      {subMsg && <p className={`${FIELD_HELP_CLASS} text-start`}>{t(subMsg)}</p>}
      </div>
      <div className="mt-2 flex items-center">
        {isImageType ? (
          <div
            className={`relative w-48 h-24 flex items-center justify-center cursor-pointer border border-hairline bg-surface-sunken/70 hover:bg-surface-sunken transition-colors p-2 rounded ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            } ${isDragging ? 'border-dashed border-champagne/60 bg-champagne/5' : ''}`}
            onClick={handleAddFile}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={t('Preview')}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <PiPlusLight className="w-6 h-6 text-fg-muted" />
            )}
            {previewUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink/20 backdrop-blur-[2px] text-white opacity-0 hover:opacity-100 transition-opacity">
                {t('Change image')}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`p-2 border border-dashed border-hairline rounded transition-colors ${
              isDragging ? 'border-champagne/60 bg-champagne/5' : ''
            } ${disabled ? 'opacity-50' : ''}`}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <div
              onClick={handleAddFile}
              className={`cursor-pointer flex items-center space-x-2 ${
                disabled ? 'cursor-not-allowed' : ''
              }`}
            >
              {file || existingFileUrl ? (
                <Badge
                  color="indigo"
                  className="max-w-40 truncate"
                  tooltip={t('Click to change')}
                  disabled={disabled}
                >
                  {getFileName()}
                </Badge>
              ) : (
                <div className="text-sm text-fg-body hover:text-fg-strong transition-colors flex items-center gap-2">
                  <PiCloudArrowUpLight className="w-4 h-4" />
                  <span>{t('Choose a file')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <input
        type="file"
        accept={accept}
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
};

export default SingleFileInput;
